import "./SuccessMetrics.scss";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import {
  WmSelect,
  WmOption,
  WmNestedSelect,
  WmOptgroup,
  WmSnackbar,
} from "@watermarkinsights/ripple-react";
import { WmSnackbarCustomEvent } from "@watermarkinsights/ripple";
import { useContext, useEffect, useReducer } from "react";
import { AppContext } from "@/contexts/AppContext";
import { notifications, Term } from "../../common/interfaces/Interface";
import { sendRequest } from "../../../common/http";

let snackbarNotifications: notifications[] = [];

interface MetricData {
  value: number;
  change: number;
  trend: "increase" | "decrease";
}

interface SuccessMetricsData {
  enrollments: MetricData;
  term_completion: MetricData;
  average_course_dfw_rates: MetricData;
}

interface TermWithPreviousTimePeriod {
  current: Term;
  previousTerm: Term | null;
  previousYearTerm: Term | null;
}

interface SuccessMetricsState {
  metricsData: SuccessMetricsData | null;
  termsWithPrevious: TermWithPreviousTimePeriod[];
  terms: Term[];
  termsLoaded: boolean;
  selectedTermUuid: string;
  selectedCompareTo: string;
  compareLabel: string;
}

interface SuccessMetricsAction {
  type: string;
  metricsData: SuccessMetricsData | null;
  termsWithPrevious: TermWithPreviousTimePeriod[];
  terms: Term[];
  selectedTermUuid: string;
  selectedCompareTo: string;
  compareLabel: string;
}

const defaultAction: SuccessMetricsAction = {
  type: "",
  metricsData: null,
  termsWithPrevious: [],
  terms: [],
  selectedTermUuid: "",
  selectedCompareTo: "",
  compareLabel: "",
};

const initialState: SuccessMetricsState = {
  metricsData: null,
  termsWithPrevious: [],
  terms: [],
  termsLoaded: false,
  selectedTermUuid: "",
  selectedCompareTo: "previous-year",
  compareLabel: "",
};

const reducer = (
  state: SuccessMetricsState,
  action: SuccessMetricsAction
): SuccessMetricsState => {
  switch (action.type) {
    case "setMetricsData":
      return { ...state, metricsData: action.metricsData };
    case "setTerms":
      return {
        ...state,
        termsWithPrevious: action.termsWithPrevious,
        terms: action.termsWithPrevious.map((t) => t.current),
        termsLoaded: true,
        selectedTermUuid:
          action.termsWithPrevious.length > 0
            ? action.termsWithPrevious[0].current.uuid
            : "",
        compareLabel: action.compareLabel,
      };
    case "selectTerm":
      return { ...state, selectedTermUuid: action.selectedTermUuid };
    case "selectCompareTo":
      return { ...state, selectedCompareTo: action.selectedCompareTo, compareLabel: action.compareLabel };
    default:
      return state;
  }
};

function resolveCompareTermUuid(
  selectedTermUuid: string,
  selectedCompareTo: string,
  termsWithPrevious: TermWithPreviousTimePeriod[]
): string {
  const entry = termsWithPrevious.find((t) => t.current.uuid === selectedTermUuid);
  if (!entry) return "";

  if (selectedCompareTo === "previous-term") {
    return entry.previousTerm?.uuid ?? selectedTermUuid;
  }

  if (selectedCompareTo === "previous-year") {
    return entry.previousYearTerm?.uuid ?? selectedTermUuid;
  }

  return selectedCompareTo || selectedTermUuid;
}

function getChangeClass(trend: "increase" | "decrease", invertBad = false): string {
  const isIncrease = trend === "increase";
  if (invertBad) {
    return isIncrease ? "negative up-change" : "positive down-change";
  }
  return isIncrease ? "positive up-change" : "negative down-change";
}

interface MetricCardProps {
  title: string;
  data: MetricData | undefined;
  formatValue: (value: number) => string;
  invertBad?: boolean;
  tellMeMoreLabel: string;
  compareLabel: string;
  onTellMeMore: () => void;
  emptyMessage?: string;
}

function MetricCard({ title, data, formatValue, invertBad = false, tellMeMoreLabel, compareLabel, onTellMeMore, emptyMessage }: MetricCardProps) {
  const intl = useIntl();
  return (
    <div className="metric">
      <h5 className="metric-title">{title}</h5>
      {emptyMessage ? (
        <p className="metric-empty-message">{emptyMessage}</p>
      ) : (
        <>
          <p className="metric-value">
            {data?.value != null ? formatValue(data.value) : "—"}
          </p>
          {compareLabel ? (
            <div className="metric-change">
              {data?.change === 0 ? (
                <span className="metric-change-label">
                  {intl.formatMessage({ id: "app.successMetrics.noChangeSince" }, { term: compareLabel })}
                </span>
              ) : (
                <>
                  <span className={`metric-change-value ${data?.trend ? getChangeClass(data.trend, invertBad) : ""}`}>
                    {data?.change != null ? `${data.change}%` : ""}
                  </span>
                  <span className="metric-change-label">
                    {data?.trend
                      ? intl.formatMessage(
                          { id: data.trend === "increase" ? "app.successMetrics.increaseSince" : "app.successMetrics.decreaseSince" },
                          { term: compareLabel }
                        )
                      : ""}
                  </span>
                </>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function SuccessMetrics() {
  const intl = useIntl();
  const { openWithSuccessMetricMessage, isChatStreaming } = useContext(AppContext);

  const tellMeMoreEnrollments = intl.formatMessage({ id: "app.successMetrics.tellMeMoreEnrollments" });
  const tellMeMoreCompletion = intl.formatMessage({ id: "app.successMetrics.tellMeMoreCompletion" });
  const tellMeMoreDfw = intl.formatMessage({ id: "app.successMetrics.tellMeMoreDfw" });
  const chatBusyWarning = intl.formatMessage({ id: "app.successMetrics.chatBusyWarning" });
  const comparePreviousTermLabel = intl.formatMessage({ id: "app.successMetrics.comparePreviousTerm" });
  const comparePreviousYearLabel = intl.formatMessage({ id: "app.successMetrics.comparePreviousYear" });
  const noPreviousTermLabel = intl.formatMessage({ id: "app.successMetrics.noPreviousTerm" });
  const noPreviousYearLabel = intl.formatMessage({ id: "app.successMetrics.noPreviousYear" });
  const noGradesDataMessage = intl.formatMessage({ id: "app.successMetrics.noGradesData" });

  const [state, dispatch] = useReducer(reducer, initialState);
  const { metricsData, termsWithPrevious, terms, termsLoaded, selectedTermUuid, selectedCompareTo, compareLabel } = state;
  const noTermsAvailable = termsLoaded && terms.length === 0;
  const gradesNotUploaded =
    metricsData != null &&
    metricsData.term_completion?.value === 0 &&
    metricsData.average_course_dfw_rates?.value === 0;

  const selectedTerm = terms.find((t) => t.uuid === selectedTermUuid) ?? null;
  const selectedEntry = termsWithPrevious.find((t) => t.current.uuid === selectedTermUuid) ?? null;
  const previousTerm = selectedEntry?.previousTerm ?? null;
  const previousYearTerm = selectedEntry?.previousYearTerm ?? null;

  function formatDate(iso: string): string {
    const [year, month, day] = iso.split("-");
    return `${month}/${day}/${year}`;
  }

  useEffect(() => {
    sendRequest(
      { uri: "/sa/terms-with-previous-time-period", camelize: true },
      (data: TermWithPreviousTimePeriod[]) =>
        dispatch({
          ...defaultAction,
          type: "setTerms",
          termsWithPrevious: data,
          compareLabel: comparePreviousYearLabel,
        })
    );
  }, []);

  useEffect(() => {
    if (!selectedTermUuid || termsWithPrevious.length === 0 || !selectedCompareTo) return;
    const compareTermUuid = resolveCompareTermUuid(selectedTermUuid, selectedCompareTo, termsWithPrevious);
    if (!compareTermUuid) return;
    sendRequest(
      {
        uri: `/sa/success-metrics/term/uuid/${selectedTermUuid}/compare/term/uuid/${compareTermUuid}`,
      },
      (data: SuccessMetricsData) =>
        dispatch({ ...defaultAction, type: "setMetricsData", metricsData: data })
    );
  }, [selectedTermUuid, selectedCompareTo, termsWithPrevious]);

  function handleTellMeMore(message: string) {
    if (isChatStreaming) {
      if (snackbarNotifications.length > 0) return;
      const snackbar: any = document.getElementById("wm-snackbar-success-metrics");
      const newNotification = { id: `snack-${Date.now()}`, message: chatBusyWarning };
      snackbarNotifications = [newNotification];
      snackbar.notifications = JSON.stringify(snackbarNotifications);
      return;
    }
    openWithSuccessMetricMessage(message);
  }

  function dismissSnackBar(ev: WmSnackbarCustomEvent<Object>) {
    const snackbar: any = document.getElementById(ev.target.id);
    snackbarNotifications = snackbarNotifications.filter(
      (snack: notifications) => snack.id !== (ev.detail as any).id
    );
    snackbar.notifications = JSON.stringify(snackbarNotifications);
  }

  function dispatchSelectCompareTo(value: string, label: string) {
    dispatch({ ...defaultAction, type: "selectCompareTo", selectedCompareTo: value, compareLabel: label });
  }

  return (
    <>
    <div className="success-metrics paper-card">
      <div className="header">
        <div className="metrics-info">
          <h3 className="title">Success Metrics</h3>
          <p className="description">
            {translate("app.successMetrics.description")}
          </p>
        </div>
      </div>
      <div className="body">
        {noTermsAvailable ? (
          <div className="sm-empty-state">
            <p className="sm-empty-state-title">
              {translate("app.successMetrics.noDataAvailable")}
            </p>
            <p className="sm-empty-state-desc">
              {translate("app.successMetrics.noDataAvailableDesc")}
            </p>
          </div>
        ) : (
        <>
        <div className="metric-info">
          <div className="metric-info-description">
            <h4 className="metric-info-title">
              {selectedTerm ? `${selectedTerm.name} Metrics` : "-"}
            </h4>
            <p className="metric-info-period">
              {selectedTerm
                ? `${formatDate(selectedTerm.startDate)} - ${formatDate(selectedTerm.endDate)}`
                : "-"}
            </p>
          </div>
          <div className="metric-selects">
            <WmSelect
              label="Term"
              labelPosition="left"
              id="select-metric-terms"
              search
              searchPlaceholder="Search terms"
            >
              {terms.map((term) => (
                <WmOption
                  id={`term-${term.uuid}`}
                  key={term.uuid}
                  selected={term.uuid === selectedTermUuid}
                  onClick={() =>
                    dispatch({ ...defaultAction, type: "selectTerm", selectedTermUuid: term.uuid })
                  }
                >
                  {term.name} ({term.termId})
                </WmOption>
              ))}
            </WmSelect>
            <WmNestedSelect
              label="Compare To"
              labelPosition="left"
              search
              searchPlaceholder="Search terms"
              id="select-metric-compare-to"
              onWmOptgroupAllDeselected={() => dispatchSelectCompareTo("", "")}
            >
              <WmOptgroup label="Time Period" >
                <WmOption
                  id="compare-previous-term"
                  key={`compare-previous-term-${selectedTermUuid}`}
                  selected={selectedCompareTo === "previous-term"}
                  onClick={() => dispatchSelectCompareTo("previous-term", comparePreviousTermLabel)}
                >
                  {previousTerm ? comparePreviousTermLabel : noPreviousTermLabel}
                </WmOption>
                <WmOption
                  id="compare-previous-year"
                  key={`compare-previous-year-${selectedTermUuid}`}
                  selected={selectedCompareTo === "previous-year"}
                  onClick={() => dispatchSelectCompareTo("previous-year", comparePreviousYearLabel)}
                >
                  {previousYearTerm ? comparePreviousYearLabel : noPreviousYearLabel}
                </WmOption>
              </WmOptgroup>
              <WmOptgroup label="Term">
                {terms.map((term) => (
                  <WmOption
                    id={`compare-${term.uuid}`}
                    key={`compare-${term.uuid}`}
                    selected={selectedCompareTo === term.uuid}
                    subinfo={`${formatDate(term.startDate)} – ${formatDate(term.endDate)}`}
                    onClick={() => dispatchSelectCompareTo(term.uuid, term.name)}
                  >
                    {term.name} ({term.termId})
                  </WmOption>
                ))}
              </WmOptgroup>
            </WmNestedSelect>
          </div>
        </div>
        <div className="metrics">
          <MetricCard
            title="Enrollments"
            data={metricsData?.enrollments}
            formatValue={(v) => v.toLocaleString()}
            compareLabel={compareLabel}
            tellMeMoreLabel={tellMeMoreEnrollments}
            onTellMeMore={() => handleTellMeMore(tellMeMoreEnrollments)}
          />
          <MetricCard
            title="Term Completion"
            data={metricsData?.term_completion}
            formatValue={(v) => `${v}%`}
            compareLabel={compareLabel}
            tellMeMoreLabel={tellMeMoreCompletion}
            onTellMeMore={() => handleTellMeMore(tellMeMoreCompletion)}
            emptyMessage={gradesNotUploaded ? noGradesDataMessage : undefined}
          />
          <MetricCard
            title="Average Course DFW Rate"
            data={metricsData?.average_course_dfw_rates}
            formatValue={(v) => `${v}%`}
            invertBad
            compareLabel={compareLabel}
            tellMeMoreLabel={tellMeMoreDfw}
            onTellMeMore={() => handleTellMeMore(tellMeMoreDfw)}
            emptyMessage={gradesNotUploaded ? noGradesDataMessage : undefined}
          />
        </div>
        </>
        )}
      </div>
    </div>
    <WmSnackbar
      notifications="[]"
      id="wm-snackbar-success-metrics"
      onWmSnackbarSnackFinished={(event) => dismissSnackBar(event)}
    />
    </>
  );
}
